class BeatStore {
    constructor() {
        this.currentAudio = null;
        this.currentBeatItem = null;
        this.playerBar = document.getElementById('beatPlayerBar');
        this.playPauseBtn = document.getElementById('playerPlayPause');
        this.playIcon = this.playPauseBtn.querySelector('.play-icon');
        this.pauseIcon = this.playPauseBtn.querySelector('.pause-icon');
        this.progressBar = document.querySelector('.progress-bar');
        this.progressFill = document.querySelector('.progress-fill');
        this.playerTime = document.querySelector('.player-time');
        this.playerTrackName = document.querySelector('.player-track-name');
        this.progressTooltip = document.getElementById('progressTooltip');
        this.playerCartBtn = document.getElementById('playerCartBtn');
        this.playerCartPrice = document.getElementById('playerCartPrice');
        this.licenseModal = document.getElementById('licenseModal');
        this.currentProductData = null;
        this.selectedLicense = null;

        this.volumeBtn = document.getElementById('playerVolumeBtn');
        this.volumeSliderTrack = document.getElementById('volumeSliderTrack');
        this.volumeSliderFill = document.getElementById('volumeSliderFill');
        
        this.volumeIcons = {
            normal: document.getElementById('volume-icon-normal'),
            mute: document.getElementById('volume-icon-mute')
        };
        
        this.lastVolume = 50; 
        this.isDraggingVolume = false; 
        this.isMuted = false; 
        this.isMobile = window.innerWidth <= 768; 
        this.isDraggingProgress = false;
        this.wasPlayingBeforeDrag = false; 

        this.beatsPerLoad = parseInt(document.getElementById('beats-per-load-config')?.dataset.beatsPerLoad || 50);
        this.currentlyDisplayedBeats = 0;
        
        this.allBeats = [];
        this.filteredBeats = [];
        this.loadMoreContainer = null;
        this.loadMoreBtn = null;
        this.loadAllBtn = null;

        this.currentSort = { column: 'date', direction: 'desc' };

        // autoplay + keyboard shortcuts
        this.autoplayEnabled = true;
        this.seekStep = 5;
        this.volumeSliderTimeout = null;

        this.init();

        this.initVolumeControls();
        this.initKeyboardControls();
        this.initScrollVolumeControl(); 
        this.initLoadMoreSystem();
    }

    isMobileDevice() {
        return window.innerWidth <= 768;
    }

    getCorrectDisplayValue() {
        return this.isMobileDevice() ? 'flex' : 'grid';
    }

    init() {
        this.setupEventListeners();
        this.setupFilters();
        this.setupSearch();
        this.setupAnimations();
        this.setupSearchClear();
        
    }

    setupResponsiveListeners() {
        let resizeTimeout;
        window.addEventListener('resize', () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                
                this.applyFilters();
            }, 250);
        });
    }

    initLoadMoreSystem() {
        this.allBeats = Array.from(document.querySelectorAll('.beat-item'));

        this.allBeats.forEach((beat, index) => {
            if (index >= this.beatsPerLoad) {
                beat.classList.add('beat-hidden-by-loadmore');
            }
        });

        const beatList = document.querySelector('.beat-list');
        if (beatList) {
            beatList.style.opacity = '0';
        }

        if (window.beatStoreConfig && window.beatStoreConfig.needsAjaxLoad) {
            this.loadRemainingBeatsViaAjax().then(() => {
                this.sortAndDisplayAllBeats();
            });
        } else {
            
            this.sortAndDisplayAllBeats();
        }
    }

    async loadRemainingBeatsViaAjax() {
        const collectionHandle = window.beatStoreConfig.collectionHandle;
        const totalProducts = window.beatStoreConfig.totalProducts;
        const productsPerPage = 250;
        const totalPages = Math.ceil(totalProducts / productsPerPage);

        for (let page = 2; page <= totalPages; page++) {
            try {
                const response = await fetch(`/collections/${collectionHandle}?page=${page}`);
                const html = await response.text();

                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');
                const newBeats = Array.from(doc.querySelectorAll('.beat-item'));
                
                if (newBeats.length > 0) {
                    
                    const beatList = document.querySelector('.beat-list');
                    newBeats.forEach(beat => {
                        this.allBeats.push(beat);
                        beatList.appendChild(beat);

                        this.attachBeatEventListeners(beat);
                    });
                }
                
            } catch (error) {
                console.error(`❌ Error loading page ${page}:`, error);
            }
        }
    }

    sortAndDisplayAllBeats() {
        const beatList = document.querySelector('.beat-list');
        
        if (beatList && this.allBeats.length > 0) {
            
            this.allBeats.sort((a, b) => {
                let dateA = parseInt(a.dataset.createdDate);
                let dateB = parseInt(b.dataset.createdDate);

                if (!dateA || isNaN(dateA) || dateA === 0) {
                    dateA = Date.now() - 1000000;
                }
                if (!dateB || isNaN(dateB) || dateB === 0) {
                    dateB = Date.now() - 1000000;
                }

                return dateB - dateA;
            });

            beatList.innerHTML = '';
            this.allBeats.forEach(item => {
                beatList.appendChild(item);
            });

            requestAnimationFrame(() => {
                beatList.style.transition = 'opacity 0.3s ease';
                beatList.style.opacity = '1';
            });
        }
        
        this.filteredBeats = [...this.allBeats];

        this.loadMoreContainer = document.querySelector('.beat-load-more-container');
        this.loadMoreBtn = document.querySelector('.beat-load-more-btn');
        this.loadAllBtn = document.querySelector('.beat-load-all-btn');
        
        if (!this.loadMoreBtn) return;

        this.loadMoreBtn.addEventListener('click', () => this.loadMoreBeats());

        if (this.loadAllBtn) {
            this.loadAllBtn.addEventListener('click', () => this.loadAllBeats());
        }

        this.updateVisibleBeats();

        requestAnimationFrame(() => {
            this.setupColumnSort();
        });
    }

    updateVisibleBeats() {
        const beatsToShow = this.currentlyDisplayedBeats + this.beatsPerLoad;

        this.filteredBeats.forEach((beat, index) => {
            if (index < beatsToShow) {
                beat.classList.remove('beat-hidden-by-loadmore');
            } else {
                beat.classList.add('beat-hidden-by-loadmore');
            }
        });
        
        this.currentlyDisplayedBeats = Math.min(beatsToShow, this.filteredBeats.length);

        this.updateLoadMoreButton();

        this.triggerBeatAnimations();
    }

    loadMoreBeats() {
        if (!this.loadMoreBtn) return;

        this.loadMoreBtn.classList.add('loading');

        setTimeout(() => {
            this.updateVisibleBeats();
            this.loadMoreBtn.classList.remove('loading');
        }, 200);
    }

    loadAllBeats() {
        if (!this.loadAllBtn) return;

        this.loadAllBtn.classList.add('loading');

        setTimeout(() => {
            
            this.currentlyDisplayedBeats = this.filteredBeats.length - this.beatsPerLoad;
            this.updateVisibleBeats();
            this.loadAllBtn.classList.remove('loading');
        }, 200);
    }

    updateLoadMoreButton() {
        if (!this.loadMoreContainer) return;
        
        const hasMoreBeats = this.currentlyDisplayedBeats < this.filteredBeats.length;
        
        if (hasMoreBeats) {
            this.loadMoreContainer.style.display = 'flex';
            
            setTimeout(() => {
                this.loadMoreContainer.classList.add('is-visible');
            }, 100);
        } else {
            this.loadMoreContainer.style.display = 'none';
            this.loadMoreContainer.classList.remove('is-visible');
        }
    }

    triggerBeatAnimations() {
        const visibleBeats = this.filteredBeats.filter((beat, index) => 
            index < this.currentlyDisplayedBeats && 
            !beat.classList.contains('is-visible')
        );
        
        visibleBeats.forEach((beat, index) => {
            setTimeout(() => {
                beat.classList.add('is-visible');
            }, index * 30); 
        });
    }
    
    setupAnimations() {
        const animatedElements = [
            
            document.querySelector('.beat-store-heading'),
            document.querySelector('.beat-collection-description'),
            document.querySelector('.beat-filters'),
            document.querySelector('.beat-search-container'),
            document.querySelector('.beat-list-header'),

            document.querySelector('.pricing-plans .section-title'),
            ...document.querySelectorAll('.plan-card')
        ];

        const observer = new IntersectionObserver((entries, observer) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('is-visible');
                    observer.unobserve(entry.target);
                }
            });
        }, {
            rootMargin: '0px 0px -20px 0px', 
            threshold: 0.05 
        });

        animatedElements.forEach((el, index) => {
            if (el) {
                el.style.transitionDelay = `${index * 50}ms`; 
                observer.observe(el);
            }
        });

        const beatObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && !entry.target.classList.contains('is-visible')) {
                    entry.target.classList.add('is-visible');
                }
            });
        }, {
            rootMargin: '50px 0px 50px 0px', 
            threshold: 0.01 
        });

        document.querySelectorAll('.beat-item').forEach(beat => {
            beatObserver.observe(beat);
        });
    }

    attachBeatEventListeners(beatItem) {
        
        beatItem.addEventListener('click', (e) => {
            const targetActionElement = e.target.closest('.beat-actions button');
            if (targetActionElement) {
                return;
            }
            this.playBeat(beatItem);
        });

        const cartBtn = beatItem.querySelector('.beat-cart-btn');
        if (cartBtn) {
            cartBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                const productHandle = beatItem.dataset.productId;
                if (productHandle) {
                    window.location.href = `/products/${productHandle}`;
                } else {
                    console.error('Product list: Product handle not available for redirection.');
                    this.showNotification('Impossible de charger les informations du produit.', 'error');
                }
            });
        }

        const downloadBtn = beatItem.querySelector('.beat-download');
        if (downloadBtn) {
            downloadBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                this.handleDownload(beatItem);
            });
        }

        const shareBtn = beatItem.querySelector('.beat-share');
        if (shareBtn) {
            shareBtn.addEventListener('click', async (e) => {
                e.stopPropagation();
                const shareUrl = shareBtn.dataset.url;
                if (navigator.share) {
                    try {
                        await navigator.share({
                            title: document.title,
                            url: shareUrl
                        });
                    } catch (error) {
                        console.error('Share failed:', error);
                    }
                } else {
                    
                    navigator.clipboard.writeText(shareUrl).then(() => {
                        this.showNotification('Lien copié dans le presse-papier!', 'success');
                    });
                }
            });
        }

        beatItem.querySelectorAll('.beat-tag').forEach(tag => {
            tag.addEventListener('click', (e) => {
                e.stopPropagation();
                const tagText = tag.getAttribute('data-tag-value');
                const searchInput = document.querySelector('.beat-search-input');
                
                searchInput.value = tagText;
                this.searchBeats(tagText);
                this.updateSearchClearVisibility();
                
                searchInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
                searchInput.focus();
                
                document.querySelectorAll('.beat-filter-select').forEach(filter => {
                    filter.value = filter.querySelector('option').value;
                });
            });
        });
    }

    setupEventListeners() {
        document.querySelectorAll('.beat-item').forEach(item => {
            item.addEventListener('click', (e) => {
                const targetActionElement = e.target.closest('.beat-actions button');
                if (targetActionElement) {
                    return;
                }
                this.playBeat(item);
            });
        });

        this.playPauseBtn.addEventListener('click', () => this.togglePlayPause());
        document.getElementById('playerPrev').addEventListener('click', () => this.playPrevious());
        document.getElementById('playerNext').addEventListener('click', () => this.playNext());
        document.getElementById('playerClose').addEventListener('click', () => this.closePlayer());

        // Setup progress bar drag functionality
        this.setupProgressBarDrag();

        if (this.progressBar) {
            this.progressBar.addEventListener('mousemove', (e) => this.updateTooltip(e));
            this.progressBar.addEventListener('mouseleave', () => this.hideTooltip());
        }

        document.querySelectorAll('.beat-cart-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                const beatItem = e.target.closest('.beat-item');
                if (beatItem) {
                    const productHandle = beatItem.dataset.productId;
                    if (productHandle) {
                        window.location.href = `/products/${productHandle}`;
                    } else {
                        console.error('Product list: Product handle not available for redirection.');
                        this.showNotification('Impossible de charger les informations du produit.', 'error');
                    }
                }
            });
        });

        document.querySelectorAll('.beat-tag').forEach(tag => {
            tag.addEventListener('click', (e) => {
                e.stopPropagation();
                const tagText = tag.getAttribute('data-tag-value');
                const searchInput = document.querySelector('.beat-search-input');

                searchInput.value = tagText;

                this.searchBeats(tagText);

                this.updateSearchClearVisibility();

                searchInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
                searchInput.focus();

                document.querySelectorAll('.beat-filter-select').forEach(filter => {
                    filter.value = filter.querySelector('option').value;
                });
            });
        });

        if (this.playerCartBtn) {
            this.playerCartBtn.addEventListener('click', (e) => {
                e.stopPropagation();

                if (this.currentProductData && this.currentProductData.productHandle) {
                    window.location.href = `/products/${this.currentProductData.productHandle}`;
                } else {
                    console.error('Player: Product data or handle not available for redirection.');
                    this.showNotification('Impossible de charger les informations du produit.', 'error');
                }
            });
        }

        document.querySelectorAll('.beat-download').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                const beatItem = e.target.closest('.beat-item');
                if (beatItem) {
                    this.handleDownload(beatItem);
                }
            });
        });

        document.querySelectorAll('.beat-share').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                e.stopPropagation();
                const beatItem = e.target.closest('.beat-item');
                if (beatItem) {
                    const shareUrl = btn.dataset.url;
                    if (navigator.share) {
                        try {
                            await navigator.share({
                                title: document.title,
                                url: shareUrl
                            });
                        } catch (error) {
                            console.error('Error sharing:', error);
                        }
                    } else {
                        try {
                            await navigator.clipboard.writeText(shareUrl);
                            alert('Lien copié dans le presse-papiers !');
                        } catch (err) {
                            console.error('Impossible de copier le lien: ', err);
                            prompt('Copiez ce lien manuellement:', shareUrl);
                        }
                    }
                }
            });
        });
    }

    setupFilters() {
        document.querySelectorAll('.beat-filter-select').forEach(filter => {
            filter.addEventListener('change', () => this.applyFilters());
        });
    }

    setupProgressBarDrag() {
        if (!this.progressBar) return;

        const setProgressFromEvent = (e) => {
            if (!this.currentAudio || !this.currentAudio.duration) return;
            
            const rect = this.progressBar.getBoundingClientRect();
            const percent = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
            this.currentAudio.currentTime = percent * this.currentAudio.duration;
        };

        const startDrag = (e) => {
            // Sauvegarder l'état de lecture actuel
            this.wasPlayingBeforeDrag = this.currentAudio && !this.currentAudio.paused;
            
            // Mettre en pause si en lecture
            if (this.wasPlayingBeforeDrag) {
                this.currentAudio.pause();
            }
            
            this.isDraggingProgress = true;
            setProgressFromEvent(e);
            
            // Ajouter une classe pour le style visuel pendant le drag
            this.progressBar.classList.add('dragging');
            
            // Empêcher la sélection de texte pendant le drag
            e.preventDefault();
        };

        const moveDrag = (e) => {
            if (this.isDraggingProgress) {
                setProgressFromEvent(e);
                // Mettre à jour le tooltip pendant le drag
                this.updateTooltip(e);
            }
        };

        const endDrag = () => {
            if (this.isDraggingProgress) {
                this.isDraggingProgress = false;
                this.progressBar.classList.remove('dragging');
                
                // Reprendre la lecture si elle était en cours avant le drag
                if (this.wasPlayingBeforeDrag && this.currentAudio) {
                    this.currentAudio.play();
                    this.wasPlayingBeforeDrag = false;
                }
            }
        };

        // Click simple pour sauter à une position
        this.progressBar.addEventListener('click', (e) => {
            if (!this.isDraggingProgress) {
                setProgressFromEvent(e);
            }
        });

        // Drag sur desktop
        this.progressBar.addEventListener('mousedown', startDrag);
        document.addEventListener('mousemove', moveDrag);
        document.addEventListener('mouseup', endDrag);

        // Support tactile pour mobile
        this.progressBar.addEventListener('touchstart', (e) => {
            // Sauvegarder l'état de lecture actuel
            this.wasPlayingBeforeDrag = this.currentAudio && !this.currentAudio.paused;
            
            // Mettre en pause si en lecture
            if (this.wasPlayingBeforeDrag) {
                this.currentAudio.pause();
            }
            
            this.isDraggingProgress = true;
            this.progressBar.classList.add('dragging');
            const touch = e.touches[0];
            setProgressFromEvent(touch);
            e.preventDefault();
        }, { passive: false });

        document.addEventListener('touchmove', (e) => {
            if (this.isDraggingProgress && e.touches.length > 0) {
                const touch = e.touches[0];
                setProgressFromEvent(touch);
            }
        }, { passive: false });

        document.addEventListener('touchend', () => {
            if (this.isDraggingProgress) {
                this.isDraggingProgress = false;
                this.progressBar.classList.remove('dragging');
                
                // Reprendre la lecture si elle était en cours avant le drag
                if (this.wasPlayingBeforeDrag && this.currentAudio) {
                    this.currentAudio.play();
                    this.wasPlayingBeforeDrag = false;
                }
            }
        });
    }

    setupSearch() {
        const searchInput = document.querySelector('.beat-search-input');
        let searchTimeout;
        searchInput.addEventListener('input', (e) => {
            clearTimeout(searchTimeout);
            searchTimeout = setTimeout(() => this.searchBeats(e.target.value), 300);
            this.updateSearchClearVisibility();
        });
    }

    setupSearchClear() {
        const searchInput = document.querySelector('.beat-search-input');
        const clearButton = document.querySelector('.beat-search-clear');
        
        if (!clearButton || !searchInput) return;

        clearButton.addEventListener('click', () => {
            searchInput.value = '';
            this.searchBeats('');
            this.updateSearchClearVisibility();
            searchInput.focus();
        });

        this.updateSearchClearVisibility();
    }
    
    updateSearchClearVisibility() {
        const searchInput = document.querySelector('.beat-search-input');
        const clearButton = document.querySelector('.beat-search-clear');
        
        if (!clearButton || !searchInput) return;
        
        if (searchInput.value.length > 0) {
            clearButton.classList.add('show');
        } else {
            clearButton.classList.remove('show');
        }
    }

    setupColumnSort() {
        const headers = document.querySelectorAll('.beat-header-sortable');
        
        if (headers.length === 0) {
            
            setTimeout(() => this.setupColumnSort(), 100);
            return;
        }
        
        headers.forEach(header => {
            header.addEventListener('click', () => {
                const column = header.dataset.sort;

                if (this.currentSort.column === column) {
                    this.currentSort.direction = this.currentSort.direction === 'asc' ? 'desc' : 'asc';
                } else {
                    this.currentSort.column = column;
                    this.currentSort.direction = 'asc';
                }

                this.updateSortIcons();

                this.sortBeats();
            });
        });
    }

    updateSortIcons() {
        document.querySelectorAll('.beat-header-sortable').forEach(header => {
            const icon = header.querySelector('.sort-icon');
            if (!icon) return;
            
            if (header.dataset.sort === this.currentSort.column) {
                header.classList.add('sorted');
                if (this.currentSort.direction === 'asc') {
                    icon.style.transform = 'rotate(0deg)';
                } else {
                    icon.style.transform = 'rotate(180deg)';
                }
            } else {
                header.classList.remove('sorted');
                icon.style.transform = 'rotate(0deg)';
            }
        });
    }

    sortBeats() {
        const beatList = document.querySelector('.beat-list');
        
        this.filteredBeats.sort((a, b) => {
            let valA, valB;
            
            switch(this.currentSort.column) {
                case 'title':
                    valA = a.dataset.title.toLowerCase();
                    valB = b.dataset.title.toLowerCase();
                    return this.currentSort.direction === 'asc' 
                        ? valA.localeCompare(valB)
                        : valB.localeCompare(valA);
                
                case 'duration':
                    valA = parseInt(a.dataset.durationSeconds || 0);
                    valB = parseInt(b.dataset.durationSeconds || 0);
                    return this.currentSort.direction === 'asc' 
                        ? valA - valB 
                        : valB - valA;
                
                case 'bpm':
                    valA = parseInt(a.dataset.bpm || 0);
                    valB = parseInt(b.dataset.bpm || 0);
                    return this.currentSort.direction === 'asc' 
                        ? valA - valB 
                        : valB - valA;
                
                case 'date':
                default:
                    valA = parseInt(a.dataset.createdDate || 0);
                    valB = parseInt(b.dataset.createdDate || 0);
                    return this.currentSort.direction === 'asc' 
                        ? valA - valB 
                        : valB - valA;
            }
        });

        beatList.innerHTML = '';
        this.filteredBeats.forEach(beat => {
            beatList.appendChild(beat);
        });

        this.currentlyDisplayedBeats = 0;
        this.updateVisibleBeats();
    }

    applyFilters() {
        const filters = {
            bpm: document.querySelector('[data-filter="bpm"]').value,
            tags: document.querySelector('[data-filter="tags"]').value,
            sort: document.querySelector('[data-filter="sort"]').value
        };

        const beatList = document.querySelector('.beat-list');

        this.allBeats.forEach((item, index) => {
            if (!item.dataset.originalOrder) {
                item.dataset.originalOrder = index;
            }
        });

        let visibleBeats = this.allBeats.filter(item => {
            const itemBpm = parseInt(item.dataset.bpm);
            const itemTags = JSON.parse(item.dataset.tags || '[]');

            let bpmMatch = filters.bpm === 'all';
            if (!bpmMatch) {
                const bpmSelect = document.querySelector('[data-filter="bpm"]');
                const selectedOption = bpmSelect.querySelector(`option[value="${filters.bpm}"]`);
                
                if (selectedOption) {
                    const minBpm = parseInt(selectedOption.dataset.min || 0);
                    const maxBpm = parseInt(selectedOption.dataset.max || 999);
                    bpmMatch = itemBpm >= minBpm && itemBpm <= maxBpm;
                }
            }

            let tagsMatch = filters.tags === 'all' || itemTags.includes(filters.tags);

            return bpmMatch && tagsMatch;
        });

        const sortValue = filters.sort || 'date-desc';
        
        if (sortValue === 'date-desc') {
            visibleBeats.sort((a, b) => {
                const dateA = parseInt(a.dataset.createdDate || 0);
                const dateB = parseInt(b.dataset.createdDate || 0);
                return dateB - dateA;
            });
        } else if (sortValue === 'date-asc') {
            visibleBeats.sort((a, b) => {
                const dateA = parseInt(a.dataset.createdDate || 0);
                const dateB = parseInt(b.dataset.createdDate || 0);
                return dateA - dateB;
            });
        }

        this.allBeats.forEach(item => {
            item.classList.add('hidden');
            item.classList.remove('is-visible');
        });

        beatList.innerHTML = '';
        visibleBeats.forEach(item => {
            item.classList.remove('hidden');
            beatList.appendChild(item);
        });

        this.filteredBeats = visibleBeats;
        this.currentlyDisplayedBeats = 0;
        this.updateVisibleBeats();

        requestAnimationFrame(() => {
            const displayValue = this.isMobileDevice() ? 'flex' : 'grid';
            visibleBeats.slice(0, this.currentlyDisplayedBeats).forEach((item, index) => {
                if (!item.classList.contains('hidden')) {
                    item.style.display = displayValue;
                    setTimeout(() => {
                        item.classList.add('is-visible');
                    }, index * 30);
                }
            });
        });
    }

    normalizeString(str) {
        if (typeof str !== 'string') return '';
        return str
            .toLowerCase()
            .normalize("NFD") 
            .replace(/[\u0300-\u036f]/g, ""); 
    }

    searchBeats(query) {
        const searchTerm = query.toLowerCase().trim();
        const beatList = document.querySelector('.beat-list');

        let matchingBeats = this.allBeats;

        if (searchTerm) {
            matchingBeats = this.allBeats.filter(item => {
                const title = item.dataset.title?.toLowerCase() || '';
                const tags = JSON.parse(item.dataset.tags || '[]').map(t => t.toLowerCase());
                const bpm = item.dataset.bpm || '';

                return title.includes(searchTerm) || 
                    tags.some(tag => tag.includes(searchTerm)) ||
                    bpm.includes(searchTerm);
            });
        }

        this.allBeats.forEach(item => {
            item.classList.add('hidden');
            item.classList.remove('is-visible');
        });

        beatList.innerHTML = '';
        matchingBeats.forEach(item => {
            item.classList.remove('hidden');
            beatList.appendChild(item);
        });

        this.filteredBeats = matchingBeats;
        this.currentlyDisplayedBeats = 0;
        this.updateVisibleBeats();

        requestAnimationFrame(() => {
            const displayValue = this.isMobileDevice() ? 'flex' : 'grid';
            matchingBeats.slice(0, this.currentlyDisplayedBeats).forEach((item, index) => {
                if (!item.classList.contains('hidden')) {
                    item.style.display = displayValue;
                    setTimeout(() => {
                        item.classList.add('is-visible');
                    }, index * 30);
                }
            });
        });
    }

    async playBeat(beatItem) {
        const audioElement = beatItem.querySelector('.beat-audio');
        if (!audioElement) return;

        const audioUrl = audioElement.dataset.src || audioElement.querySelector('source')?.src;
        if (!audioUrl) return;

        if (this.currentBeatItem === beatItem && this.currentAudio && !this.currentAudio.paused) {
            this.togglePlayPause();
            return;
        }

        // save volume before switching tracks
        if (this.currentAudio) {
            this.lastVolume = Math.round(this.currentAudio.volume * 100);
            this.currentAudio.pause();
            if (this.currentBeatItem) {
                this.currentBeatItem.classList.remove('playing');
            }
        }

        const volumeToUse = this.lastVolume;

        this.currentAudio = new Audio(audioUrl);
        this.currentAudio.volume = volumeToUse / 100; 
        this.currentBeatItem = beatItem;

        this.currentAudio.addEventListener('timeupdate', () => this.updateProgress());
        this.currentAudio.addEventListener('loadedmetadata', () => this.updateTimeDisplay());
        this.currentAudio.addEventListener('ended', () => {
            this.updatePlayPauseButton(false);
            if (this.currentBeatItem) {
                this.currentBeatItem.classList.remove('playing');
            }
            if (this.autoplayEnabled) {
                this.playNext();
            }
        });

        this.currentAudio.addEventListener('volumechange', () => {
            if (!this.isDraggingVolume) { 
                const newVolumePercent = Math.round(this.currentAudio.volume * 100);
                this.updateVolumeIcon(newVolumePercent);
                if (this.volumeSliderFill) {
                   this.volumeSliderFill.style.height = `${newVolumePercent}%`;
                }
                if (newVolumePercent > 0) {
                    this.lastVolume = newVolumePercent;
                }
            }
        });

        // sync volume UI
        this.updateVolumeIcon(volumeToUse);
        if (this.volumeSliderFill) {
            this.volumeSliderFill.style.height = `${volumeToUse}%`;
        }

        this.updatePlayerUI(beatItem);
        beatItem.classList.add('playing');
        this.showPlayer();

        try {
            await this.currentAudio.play();
            this.updatePlayPauseButton(true);
        } catch (error) {
            console.error('Play error:', error);
            if (this.currentBeatItem) {
                this.currentBeatItem.classList.remove('playing');
            }
        }
    }

    showPlayer() {
        this.playerBar.style.display = 'block';
        setTimeout(() => {
            this.playerBar.classList.add('active');
        }, 10);
    }

    closePlayer() {
        if (this.currentAudio) {
            this.currentAudio.pause();
            this.currentAudio = null;
        }

        if (this.currentBeatItem) {
            this.currentBeatItem.classList.remove('playing');
            this.currentBeatItem = null;
        }

        this.playerBar.classList.remove('active');
        setTimeout(() => {
            this.playerBar.style.display = 'none';
        }, 300);
    }

    togglePlayPause() {
        if (!this.currentAudio) return;

        if (this.currentAudio.paused) {
            this.currentAudio.play().then(() => {
                this.updatePlayPauseButton(true);
                if (this.currentBeatItem) {
                    this.currentBeatItem.classList.add('playing');
                }
            });
        } else {
            this.currentAudio.pause();
            this.updatePlayPauseButton(false);
            if (this.currentBeatItem) {
                this.currentBeatItem.classList.remove('playing');
            }
        }
    }

    playNext() {
        const allFilteredBeats = Array.from(document.querySelectorAll('.beat-item:not(.hidden)'));
        
        if (!this.currentBeatItem || allFilteredBeats.length === 0) return;

        const currentIndex = allFilteredBeats.indexOf(this.currentBeatItem);
        
        if (currentIndex + 1 >= allFilteredBeats.length) return;
        
        const nextBeat = allFilteredBeats[currentIndex + 1];
        
        if (nextBeat.classList.contains('beat-hidden-by-loadmore')) {
            this.loadMoreAndPlay(nextBeat);
        } else {
            this.playBeat(nextBeat);
            this.scrollToBeat(nextBeat);
        }
    }

    playPrevious() {
        const allFilteredBeats = Array.from(document.querySelectorAll('.beat-item:not(.hidden)'));
        
        if (!this.currentBeatItem || allFilteredBeats.length === 0) return;

        const currentIndex = allFilteredBeats.indexOf(this.currentBeatItem);
        
        if (currentIndex <= 0) return;
        
        const prevBeat = allFilteredBeats[currentIndex - 1];
        this.playBeat(prevBeat);
        this.scrollToBeat(prevBeat);
    }

    loadMoreAndPlay(beatToPlay) {
        if (!this.loadMoreBtn) {
            this.playBeat(beatToPlay);
            this.scrollToBeat(beatToPlay);
            return;
        }

        const loadUntilVisible = () => {
            if (!beatToPlay.classList.contains('beat-hidden-by-loadmore')) {
                this.playBeat(beatToPlay);
                this.scrollToBeat(beatToPlay);
                return;
            }
            
            this.loadMoreBtn.classList.add('loading');
            
            setTimeout(() => {
                this.updateVisibleBeats();
                this.loadMoreBtn.classList.remove('loading');
                
                if (beatToPlay.classList.contains('beat-hidden-by-loadmore')) {
                    loadUntilVisible();
                } else {
                    this.playBeat(beatToPlay);
                    this.scrollToBeat(beatToPlay);
                }
            }, 100);
        };
        
        loadUntilVisible();
    }

    scrollToBeat(beatElement) {
        if (!beatElement) return;
        
        setTimeout(() => {
            const rect = beatElement.getBoundingClientRect();
            const playerHeight = this.playerBar?.offsetHeight || 80;
            const beatHeight = beatElement.offsetHeight || 60;
            
            // scroll 1 beat before it goes out of view
            const topMargin = beatHeight;
            const bottomMargin = playerHeight + beatHeight + 20;
            
            const isVisible = rect.top >= topMargin && rect.bottom <= (window.innerHeight - bottomMargin);
            
            if (!isVisible) {
                beatElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 50);
    }

    updatePlayPauseButton(isPlaying) {
        this.playIcon.style.display = isPlaying ? 'none' : 'block';
        this.pauseIcon.style.display = isPlaying ? 'block' : 'none';
    }

    updateProgress() {
        if (!this.currentAudio || !this.currentAudio.duration) return;

        const percent = (this.currentAudio.currentTime / this.currentAudio.duration) * 100;

        const customColor = getComputedStyle(this.progressFill).backgroundColor || '#000000';

        this.progressFill.style.cssText = `
            width: ${percent}% !important;
            background-color: ${customColor} !important;
            opacity: 1 !important;
            visibility: visible !important;
            display: block !important;
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            height: 100% !important;
            z-index: 2147483647 !important;
        `;

        this.updateTimeDisplay();
    }

    updateTimeDisplay() {
        if (!this.currentAudio) {
            this.playerTime.textContent = "0:00 / 0:00";
            return;
        }

        const current = this.formatTime(this.currentAudio.currentTime);
        const duration = this.formatTime(this.currentAudio.duration || 0);
        this.playerTime.textContent = `${current} / ${duration}`;
    }

    formatTime(seconds) {
        if (isNaN(seconds)) return '0:00';

        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    }

    updatePlayerUI(beatItem) {
        const title = beatItem.querySelector('.beat-title').textContent;
        const coverImgSrc = beatItem.querySelector('.beat-cover img')?.src;
        const price = beatItem.querySelector('.beat-cart-btn span')?.textContent;
        const productHandle = beatItem.dataset.productId;

        this.playerTrackName.textContent = title;

        const playerAlbumCover = document.getElementById('playerAlbumCover');
        if (coverImgSrc) {
            playerAlbumCover.innerHTML = `<img src="${coverImgSrc}" alt="${title}">`;
        } else {
            playerAlbumCover.innerHTML = '<div class="player-album-cover-placeholder"></div>';
        }

        if (this.playerCartBtn && price) {
            if (this.playerCartPrice) {
                this.playerCartPrice.textContent = price;
            }
            this.playerCartBtn.style.display = 'flex';
        }

        this.currentProductData = {
            productHandle: productHandle,
            title: title,
            coverImgSrc: coverImgSrc,
            price: price
        };
    }
    
    updateTooltip(e) {
        if (!this.currentAudio || !this.currentAudio.duration || !this.progressTooltip) return;
    
        const rect = this.progressBar.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const percent = Math.max(0, Math.min(1, x / rect.width));
        const hoverTime = percent * this.currentAudio.duration;
    
        this.progressTooltip.textContent = this.formatTime(hoverTime);
        this.progressTooltip.style.left = `${x}px`;
        this.progressTooltip.style.opacity = '1';
        
        // Appliquer les couleurs du bouton panier au tooltip
        if (this.playerCartBtn) {
            const computedStyle = window.getComputedStyle(this.playerCartBtn);
            const backgroundColor = computedStyle.backgroundColor;
            const textColor = computedStyle.color;
            
            this.progressTooltip.style.backgroundColor = backgroundColor;
            this.progressTooltip.style.color = textColor;
        }
    }

    hideTooltip() {
        if (!this.progressTooltip) return;
        this.progressTooltip.style.opacity = '0';
    }

    async showLicenseModal(beatItem) {
        const productHandle = beatItem.dataset.productId;
        const variantId = beatItem.dataset.variantId;
        const title = beatItem.querySelector('.beat-title').textContent;
        const price = beatItem.querySelector('.beat-cart-btn span')?.textContent;
        const coverImg = beatItem.querySelector('.beat-cover img');

        if (!productHandle) {
            this.showNotification('Erreur: Produit non trouvé', 'error');
            return;
        }

        this.licenseModal.style.display = 'block';
        document.body.style.overflow = 'hidden';

        const modalProductInfo = document.getElementById('modalProductInfo');
        modalProductInfo.innerHTML = `
            <div style="display: flex; align-items: center; gap: 15px;">
                ${coverImg ? `<img src="${coverImg.src}" alt="${title}" style="width: 60px; height: 60px; border-radius: 8px; object-fit: cover;">` : '<div style="width: 60px; height: 60px; background: #333; border-radius: 8px;"></div>'}
                <div>
                    <h3 style="margin: 0 0 5px 0; color: #fff; font-size: 18px;">${title}</h3>
                    <p style="margin: 0; color: #888; font-size: 14px;">Prix de base: ${price}</p>
                </div>
            </div>
        `;

        try {
            const response = await fetch(`/products/${productHandle}.js`);
            const product = await response.json();

            const licenseOptions = document.getElementById('licenseOptions');
            licenseOptions.innerHTML = '';

            product.variants.forEach(variant => {
                const option = document.createElement('div');
                option.className = 'license-option';
                option.dataset.variantId = variant.id;

                const licenseName = variant.title || 'Licence Standard';
                const licensePrice = new Intl.NumberFormat('fr-FR', {
                    style: 'currency',
                    currency: 'EUR'
                }).format(variant.price / 100);

                option.innerHTML = `
                    <div class="license-option-header">
                        <span class="license-option-name">${licenseName}</span>
                        <span class="license-option-price">${licensePrice}</span>
                    </div>
                    <div class="license-option-description">
                        ${this.getLicenseDescription(licenseName)}
                    </div>
                    ${!variant.available ? '<p class="license-option-unavailable">Non disponible</p>' : ''}
                `;

                if (!variant.available) {
                    option.classList.add('disabled');
                }

                option.addEventListener('click', () => {
                    if (variant.available) {
                        document.querySelectorAll('.license-option').forEach(opt => opt.classList.remove('selected'));
                        option.classList.add('selected');
                        this.selectedLicense = variant;
                    }
                });

                licenseOptions.appendChild(option);
            });

            const addButton = document.createElement('button');
            addButton.className = 'license-add-btn';
            addButton.textContent = 'Ajouter au panier';
            addButton.addEventListener('click', () => this.addToCart());
            licenseOptions.appendChild(addButton);

        } catch (error) {
            console.error('Erreur chargement produit:', error);
            document.getElementById('licenseOptions').innerHTML = '<p class="error-message">Erreur lors du chargement des licences</p>';
        }

        const closeModal = document.getElementById('closeModal');
        const modalOverlay = this.licenseModal.querySelector('.license-modal-overlay');

        const closeHandler = () => {
            this.licenseModal.style.display = 'none';
            document.body.style.overflow = '';
            this.selectedLicense = null;
        };

        closeModal.onclick = closeHandler;
        modalOverlay.onclick = closeHandler;
    }

    getLicenseDescription(licenseName) {
        const descriptions = {
            'Basic': 'Usage personnel uniquement, pas de revente',
            'Premium': 'Usage commercial, jusqu\'à 10,000 copies',
            'Exclusive': 'Droits exclusifs, le beat sera retiré de la vente',
            'Standard': 'Licence d\'utilisation musicale'
        };

        for (const [key, desc] of Object.entries(descriptions)) {
            if (licenseName.toLowerCase().includes(key.toLowerCase())) {
                return desc;
            }
        }

        return 'Licence d\'utilisation musicale';
    }

    async addToCart() {
        if (!this.selectedLicense) {
            alert('Veuillez sélectionner une licence');
            return;
        }

        try {
            const formData = new FormData();
            formData.append('items[0][id]', this.selectedLicense.id);
            formData.append('items[0][quantity]', '1');
            formData.append('sections', 'cart-notification-product,cart-notification-button,cart-icon-bubble');

            const response = await fetch('/cart/add.js', {
                method: 'POST',
                body: formData
            });

            if (!response.ok) {
                throw new Error('Erreur lors de l\'ajout au panier');
            }

            const data = await response.json();

            this.licenseModal.style.display = 'none';
            document.body.style.overflow = '';
            this.selectedLicense = null;

            this.showShopifyNotification(data);

        } catch (error) {
            console.error('Erreur:', error);
            this.showNotification('Erreur lors de l\'ajout au panier', 'error');
        }
    }

    showShopifyNotification(data) {
        const cartNotification = document.querySelector('cart-notification');

        if (cartNotification && cartNotification.renderContents) {
            const parsedState = {
                key: data.items[0].key,
                sections: {}
            };

            if (data.sections) {
                parsedState.sections = data.sections;
            }
            cartNotification.renderContents(parsedState);

        } else {
            const event = new CustomEvent('cart:add', {
                detail: {
                    items: data.items,
                    sections: data.sections
                }
            });
            document.dispatchEvent(event);

            if (data.sections && data.sections['cart-icon-bubble']) {
                this.updateCartBubble(data.sections['cart-icon-bubble']);
            }
            const productTitle = data.items?.[0]?.product_title || 'Produit';
            this.showNotification(`"${productTitle}" ajouté au panier!`, 'success');
        }
    }

    updateCartBubble(bubbleHtml) {
        if (!bubbleHtml) return;

        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = bubbleHtml;
        const newBubble = tempDiv.querySelector('.cart-count-bubble, .cart-icon-bubble');

        const existingBubbles = document.querySelectorAll('.cart-count-bubble, .cart-icon-bubble');
        existingBubbles.forEach(bubble => {
            if (newBubble && bubble.parentNode) {
                bubble.parentNode.replaceChild(newBubble.cloneNode(true), bubble);
            }
        });
    }

    showNotification(message, type = 'success') {
        const notification = document.createElement('div');
        notification.className = `beat-notification beat-notification--${type}`;
        notification.textContent = message;
        document.body.appendChild(notification);

        setTimeout(() => {
            notification.classList.add('show');
        }, 10);

        setTimeout(() => {
            notification.classList.remove('show');
            setTimeout(() => {
                notification.remove();
            }, 300);
        }, 3000);
    }

    handleDownload(beatItem) {
        
        const popupConfig = document.getElementById('popup-free-download-config');
        if (!popupConfig) {
            this.showNotification('Configuration du popup non trouvée', 'error');
            return;
        }

        const title = beatItem.dataset.title || beatItem.querySelector('.beat-title').textContent;
        const coverImg = beatItem.dataset.img || beatItem.querySelector('.beat-cover img')?.src;
        const audioUrl = beatItem.querySelector('.beat-audio').dataset.src;

        if (!audioUrl) {
            this.showNotification('Pas de fichier audio disponible pour le téléchargement', 'error');
            return;
        }

        this.showDownloadPopup({
            title: popupConfig.dataset.popupTitle + ' : ' + title,
            subtitle: popupConfig.dataset.popupSubtitle,
            buttonText: popupConfig.dataset.popupButton,
            coverImg: coverImg,
            beatTitle: title,
            audioUrl: audioUrl
        });
    }

    showDownloadPopup(config) {
        const existingPopup = document.getElementById('free-download-popup');
        if (existingPopup) {
            existingPopup.remove();
        }

        const popup = document.createElement('div');
        popup.id = 'free-download-popup';
        const colorScheme = document.getElementById('popup-free-download-config').dataset.colorScheme || '';
        popup.className = 'free-download-popup ' + colorScheme + ' gradient';
        
        popup.innerHTML = `
            <div class="free-download-popup__content">
                <div class="free-download-popup__bg">
                    ${config.coverImg ? `<img class="free-download-popup__image" src="${config.coverImg}" alt="${config.beatTitle}" onload="this.classList.add('loaded')">` : ''}
                </div>
                
                <button class="free-download-popup__close" type="button">×</button>
                
                <div class="free-download-popup__fields">
                    <h3 class="popup-title">${config.title}</h3>
                    <p class="popup-subtitle">${config.subtitle}</p>
                    
                    <form class="popup-form" data-audio-url="${config.audioUrl}">
                        <div class="popup-field">
                            <label for="popup-firstname">First name</label>
                            <input type="text" id="popup-firstname" name="firstName" required>
                        </div>
                        
                        <div class="popup-field">
                            <label for="popup-lastname">Last name</label>
                            <input type="text" id="popup-lastname" name="lastName" required>
                        </div>
                        
                        <div class="popup-field">
                            <label for="popup-email">Email adress</label>
                            <input type="email" id="popup-email" name="email" required>
                        </div>
                        
                        <div class="popup-privacy">
                            <input type="checkbox" id="popup-privacy" required>
                            <label for="popup-privacy">I agree to receive information by email</label>
                        </div>
                        
                        <button type="submit" class="popup-submit">${config.buttonText}</button>
                    </form>
                </div>
            </div>
        `;

        document.body.appendChild(popup);
        document.body.style.overflow = 'hidden';

        const closeBtn = popup.querySelector('.free-download-popup__close');
        const form = popup.querySelector('.popup-form');
        
        closeBtn.addEventListener('click', () => this.closeDownloadPopup());
        popup.addEventListener('click', (e) => {
            if (e.target === popup) this.closeDownloadPopup();
        });

        form.addEventListener('submit', (e) => {
            e.preventDefault();
            this.handleDownloadSubmit(form);
        });

        requestAnimationFrame(() => {
            popup.style.opacity = '1';
            popup.style.visibility = 'visible';
        });
    }

    closeDownloadPopup() {
        const popup = document.getElementById('free-download-popup');
        if (popup) {
            popup.style.opacity = '0';
            document.body.style.overflow = '';
            setTimeout(() => {
                popup.remove();
            }, 300);
        }
    }

    async handleDownloadSubmit(form) {
        
        const fd = new FormData(form);
        const firstName = fd.get('firstName') || fd.get('first_name') || fd.get('popup-firstname') || '';
        const lastName  = fd.get('lastName')  || fd.get('last_name')  || fd.get('popup-lastname') || '';
        const email     = fd.get('email')     || fd.get('emailAddress') || fd.get('popup-email') || '';
        const beatTitle = (document.querySelector('.popup-title')?.innerText) || (fd.get('beatTitle')) || 'Unknown Beat';
        const audioUrl  = form.dataset.audioUrl || '';

        const payload = {
            beatTitle: beatTitle,
            email: email,
            name: (firstName + ' ' + lastName).trim(),
            lang: navigator.language || navigator.userLanguage || 'en',
            downloadUrl: audioUrl
        };

        console.log('Payload envoyé à Google Script :', payload);

        const GAS_URL = 'https://script.google.com/macros/s/AKfycbxv9M8NbqHu2hFj_Uyd2WA2qHhCqU-aRHGE1yL6OdWp9QpTmwW_bZS_X3AUQSM36LVFoA/exec';

        try {
            const iframeName = 'gsPostFrame';
            let iframe = document.querySelector(`iframe[name="${iframeName}"]`);
            if (!iframe) {
                iframe = document.createElement('iframe');
                iframe.name = iframeName;
                iframe.style.display = 'none';
                document.body.appendChild(iframe);
            }

            const postForm = document.createElement('form');
            postForm.method = 'POST';
            postForm.action = GAS_URL;
            postForm.target = iframeName;
            postForm.style.display = 'none';

            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'data';
            input.value = JSON.stringify(payload);
            postForm.appendChild(input);

            document.body.appendChild(postForm);
            postForm.submit();

            setTimeout(() => postForm.remove(), 2500);

            this.showNotification("Merci ! Le lien de téléchargement a été envoyé par e-mail.", "success");
        } catch (err) {
            console.error('iframe POST failed, fallback fetch', err);

            try {
                await fetch(GAS_URL, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
                    body: new URLSearchParams({ data: JSON.stringify(payload) })
                });
                this.showNotification("Merci ! Le lien de téléchargement a été envoyé par e-mail.", "success");
            } catch (err2) {
                console.error('Fallback fetch failed', err2);
                this.showNotification("Une erreur est survenue. Réessayez plus tard.", "error");
            }
        }

        this.closeDownloadPopup();
    }

    initVolumeControls() {
        if (!this.volumeBtn || !this.volumeSliderTrack) return;

        const volumeControl = this.volumeBtn.closest('.player-volume-control');
        let sliderShowTimeout;

        const preventDefaults = (e) => {
            e.preventDefault();
            e.stopPropagation();
        };

        const clearStuckStates = () => {
            this.volumeBtn.style.transform = '';
            this.volumeBtn.style.backgroundColor = '';
            this.volumeBtn.blur(); 

            this.volumeBtn.offsetHeight;
        };

        const setVolumeFromEvent = (e) => {
            if (!this.currentAudio) return;
            
            const trackRect = this.volumeSliderTrack.getBoundingClientRect();
            const clientY = e.touches ? e.touches[0].clientY : e.clientY;
            let volumePercent = ((trackRect.bottom - clientY) / trackRect.height) * 100;
            volumePercent = Math.max(0, Math.min(100, volumePercent));
            
            this.setVolume(volumePercent);
        };

        if (!this.isMobileDevice()) {
            this.volumeBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                clearStuckStates();
                
                if (!this.currentAudio) return;
                
                if (this.currentAudio.volume > 0) {
                    this.lastVolume = this.currentAudio.volume * 100;
                    this.setVolume(0);
                } else {
                    const volumeToRestore = this.lastVolume > 5 ? this.lastVolume : 50;
                    this.setVolume(volumeToRestore);
                }
            });

            volumeControl.addEventListener('mouseenter', () => {
                clearTimeout(sliderShowTimeout);
                volumeControl.classList.add('show-slider');
            });

            volumeControl.addEventListener('mouseleave', () => {
                sliderShowTimeout = setTimeout(() => {
                    volumeControl.classList.remove('show-slider');
                }, 200);
            });

        } else {
            
            this.volumeBtn.addEventListener('touchstart', preventDefaults, { passive: false });
            this.volumeBtn.addEventListener('touchend', (e) => {
                preventDefaults(e);
                clearStuckStates();
                
                if (volumeControl.classList.contains('show-slider')) {
                    volumeControl.classList.remove('show-slider');
                } else {
                    volumeControl.classList.add('show-slider');
                }
            }, { passive: false });

            document.addEventListener('touchstart', (e) => {
                if (!volumeControl.contains(e.target)) {
                    volumeControl.classList.remove('show-slider');
                }
            });
        }

        let isDragging = false;

        const startDrag = (e) => {
            isDragging = true;
            this.isDraggingVolume = true;
            setVolumeFromEvent(e);
            
            if (e.type === 'touchstart') {
                preventDefaults(e);
            }
        };

        const moveDrag = (e) => {
            if (isDragging) {
                setVolumeFromEvent(e);
                if (e.type === 'touchmove') {
                    preventDefaults(e);
                }
            }
        };

        const endDrag = () => {
            isDragging = false;
            this.isDraggingVolume = false;
        };

        this.volumeSliderTrack.addEventListener('mousedown', startDrag);
        this.volumeSliderTrack.addEventListener('touchstart', startDrag, { passive: false });
        
        document.addEventListener('mousemove', moveDrag);
        document.addEventListener('touchmove', moveDrag, { passive: false });
        
        document.addEventListener('mouseup', endDrag);
        document.addEventListener('touchend', endDrag);

        this.setVolume(50);
    }
    
    setVolume(volume) {
        const newVolume = Math.max(0, Math.min(100, volume));
        
        if (newVolume > 0) {
            this.lastVolume = newVolume;
        }

        if (this.volumeSliderFill) {
            const volumeFillColor = getComputedStyle(this.volumeSliderFill).backgroundColor || '#f5deb3';
            this.volumeSliderFill.style.cssText = `
                height: ${newVolume}% !important;
                background-color: ${volumeFillColor} !important;
                opacity: 1 !important;
                visibility: visible !important;
                display: block !important;
                position: absolute !important;
                bottom: 0 !important;
                left: 0 !important;
                width: 100% !important;
                z-index: 2147483647 !important;
            `;
        }

        this.updateVolumeIcon(newVolume);

        if (!this.currentAudio) return;

        this.currentAudio.volume = newVolume / 100;
        this.isMuted = (newVolume === 0);
    }
    
    updateVolumeIcon(volume) {
        if (!this.volumeIcons.normal || !this.volumeIcons.mute) {
            return;
        }
        
        this.volumeIcons.normal.style.display = 'none';
        this.volumeIcons.mute.style.display = 'none';

        if (volume === 0) {
            this.volumeIcons.mute.style.display = 'block';
        } else {
            this.volumeIcons.normal.style.display = 'block';
        }
    }

    // keyboard shortcuts
    initKeyboardControls() {
        document.addEventListener('keydown', (e) => {
            if (this.isInputFocused()) return;
            if (!this.currentAudio) return;

            switch (e.key) {
                case 'ArrowRight':
                    e.preventDefault();
                    this.seekForward();
                    break;
                    
                case 'ArrowLeft':
                    e.preventDefault();
                    this.seekBackward();
                    break;
                    
                case ' ':
                    e.preventDefault();
                    this.togglePlayPause();
                    break;
                    
                case 'ArrowUp':
                    e.preventDefault();
                    this.playPrevious();
                    break;
                    
                case 'ArrowDown':
                    e.preventDefault();
                    this.playNext();
                    break;

                case 'n':
                case 'N':
                    e.preventDefault();
                    this.playNext();
                    break;

                case 'p':
                case 'P':
                    e.preventDefault();
                    this.playPrevious();
                    break;

                case 'm':
                case 'M':
                    e.preventDefault();
                    this.toggleMute();
                    break;
            }
        });
    }

    isInputFocused() {
        const activeElement = document.activeElement;
        const tagName = activeElement?.tagName?.toLowerCase();
        return tagName === 'input' || tagName === 'textarea' || activeElement?.isContentEditable;
    }

    seekForward() {
        if (!this.currentAudio || !this.currentAudio.duration) return;
        const newTime = Math.min(this.currentAudio.currentTime + this.seekStep, this.currentAudio.duration);
        this.currentAudio.currentTime = newTime;
    }

    seekBackward() {
        if (!this.currentAudio || !this.currentAudio.duration) return;
        const newTime = Math.max(this.currentAudio.currentTime - this.seekStep, 0);
        this.currentAudio.currentTime = newTime;
    }

    toggleMute() {
        if (!this.currentAudio) return;
        
        if (this.currentAudio.volume > 0) {
            this.lastVolume = this.currentAudio.volume * 100;
            this.setVolume(0);
        } else {
            const volumeToRestore = this.lastVolume > 5 ? this.lastVolume : 50;
            this.setVolume(volumeToRestore);
        }
        this.showVolumeSlider();
    }

    toggleAutoplay() {
        this.autoplayEnabled = !this.autoplayEnabled;
        const status = this.autoplayEnabled ? 'activé' : 'désactivé';
        this.showNotification(`Autoplay ${status}`, 'success');
    }

    showVolumeSlider() {
        const volumeControl = this.volumeBtn?.closest('.player-volume-control');
        if (!volumeControl) return;

        if (this.volumeSliderTimeout) {
            clearTimeout(this.volumeSliderTimeout);
        }

        volumeControl.classList.add('show-slider');

        this.volumeSliderTimeout = setTimeout(() => {
            volumeControl.classList.remove('show-slider');
        }, 1500);
    }

    // scroll on player = volume control
    initScrollVolumeControl() {
        if (!this.playerBar) return;

        this.playerBar.addEventListener('wheel', (e) => {
            if (!this.currentAudio) return;
            
            e.preventDefault();
            
            const currentVolume = this.currentAudio.volume * 100;
            
            if (e.deltaY < 0) {
                this.setVolume(Math.min(currentVolume + 5, 100));
            } else {
                this.setVolume(Math.max(currentVolume - 5, 0));
            }
            
            this.showVolumeSlider();
        }, { passive: false });
    }
}

document.addEventListener('DOMContentLoaded', () => {
    if (document.querySelector('.beat-store-section')) {
        new BeatStore();
    }
});